import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, FileText, Check, Edit2, AlertTriangle, Target, Lightbulb, ClipboardList, ChevronDown, ChevronUp } from "lucide-react";
import { format, parseISO } from "date-fns";

interface AISessionNotesProps {
  clinicianId: string;
  sessions: any[];
}

interface AISummary {
  id: string;
  session_id: string;
  raw_notes: string | null;
  ai_summary: string | null;
  key_themes: string[] | null;
  interventions_used: string[] | null;
  client_insights: string[] | null;
  risk_flags: string[] | null;
  homework_suggestions: string[] | null;
  follow_up_topics: string[] | null;
  is_approved: boolean;
  approved_at: string | null;
  generated_at: string | null;
}

export function AISessionNotes({ clinicianId, sessions }: AISessionNotesProps) {
  const { toast } = useToast();
  const [summaries, setSummaries] = useState<AISummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedSession, setSelectedSession] = useState<string>("");
  const [rawNotes, setRawNotes] = useState("");
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [expandedSummary, setExpandedSummary] = useState<string | null>(null);

  useEffect(() => {
    fetchSummaries();
  }, [clinicianId]);

  const fetchSummaries = async () => {
    const { data, error } = await supabase
      .from("ai_session_summaries")
      .select("*")
      .eq("clinician_id", clinicianId)
      .order("generated_at", { ascending: false });

    if (error) {
      toast({ title: "Error", description: "Failed to load AI summaries", variant: "destructive" });
    } else {
      setSummaries(data || []);
    }
    setLoading(false);
  };

  const handleGenerateSummary = async () => {
    if (!selectedSession || !rawNotes.trim()) return;

    setGenerating(true);
    
    try {
      // Get session info for context
      const session = sessions.find(s => s.id === selectedSession);
      const clientName = session?.client_name || "Client";
      const sessionDate = session?.session_date ? format(parseISO(session.session_date), "MMMM d, yyyy") : "";
      
      // Call edge function for AI generation
      const { data, error: fnError } = await supabase.functions.invoke('generate-clinician-summary', {
        body: {
          rawNotes,
          clientName,
          sessionDate,
          sessionType: "Individual therapy"
        }
      });

      if (fnError) {
        throw new Error(fnError.message || 'Failed to generate summary');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      const aiGenerated = data?.summary;
      
      if (!aiGenerated) {
        throw new Error('No summary generated');
      }

      const { error } = await supabase.from("ai_session_summaries").insert({
        clinician_id: clinicianId,
        session_id: selectedSession,
        raw_notes: rawNotes,
        ai_summary: aiGenerated.ai_summary,
        key_themes: aiGenerated.key_themes,
        interventions_used: aiGenerated.interventions_used,
        client_insights: aiGenerated.client_insights,
        risk_flags: aiGenerated.risk_flags,
        homework_suggestions: aiGenerated.homework_suggestions,
        follow_up_topics: aiGenerated.follow_up_topics,
        is_approved: false,
        generated_at: new Date().toISOString()
      });

      if (error) {
        throw new Error('Failed to save summary');
      }

      toast({ title: "Success", description: "AI summary generated! Please review before approving." });
      setShowGenerateDialog(false);
      setSelectedSession("");
      setRawNotes("");
      fetchSummaries();
      
    } catch (error: any) {
      console.error('Summary generation error:', error);
      toast({ 
        title: "Error", 
        description: error.message || "Failed to generate summary. Please try again.", 
        variant: "destructive" 
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleApproveSummary = async (summaryId: string) => {
    const { error } = await supabase
      .from("ai_session_summaries")
      .update({ is_approved: true, approved_at: new Date().toISOString() })
      .eq("id", summaryId);

    if (error) {
      toast({ title: "Error", description: "Failed to approve summary", variant: "destructive" });
    } else {
      toast({ title: "Approved", description: "Summary has been approved and saved to client record" });
      fetchSummaries();
    }
  };

  const getSessionInfo = (sessionId: string) => {
    const session = sessions.find(s => s.id === sessionId);
    return session ? {
      date: format(parseISO(session.session_date), "MMM d, yyyy"),
      client: session.client_name || "Client",
      time: session.session_time?.slice(0, 5)
    } : { date: "Unknown", client: "Unknown", time: "" };
  };

  const completedSessions = sessions.filter(s => s.status === "completed");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">AI Session Notes</h3>
          <p className="text-sm text-muted-foreground">Generate and review AI-powered session summaries</p>
        </div>
        <Button className="gap-2" onClick={() => setShowGenerateDialog(true)}>
          <Sparkles className="h-4 w-4" />
          Generate Summary
        </Button>
      </div>

      {/* Generate Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Generate AI Session Summary
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Select Session</Label>
              <Select value={selectedSession} onValueChange={setSelectedSession}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a completed session" />
                </SelectTrigger>
                <SelectContent>
                  {completedSessions.map(session => (
                    <SelectItem key={session.id} value={session.id}>
                      {session.client_name} - {format(parseISO(session.session_date), "MMM d, yyyy")} at {session.session_time?.slice(0, 5)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Your Session Notes (Raw)</Label>
              <Textarea
                value={rawNotes}
                onChange={(e) => setRawNotes(e.target.value)}
                placeholder="Enter your session notes here. The AI will analyse these to generate a structured summary, identify key themes, suggest homework, and flag any risk concerns..."
                rows={10}
                className="font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The AI will analyse your notes to extract themes, interventions, insights, and suggestions.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleGenerateSummary} 
                disabled={!selectedSession || !rawNotes.trim() || generating}
                className="gap-2"
              >
                {generating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Generate Summary
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Summaries List */}
      {summaries.length === 0 ? (
        <Card className="bg-card/50 border-border/30">
          <CardContent className="py-12 text-center">
            <Sparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">No AI Summaries Yet</h3>
            <p className="text-muted-foreground mb-4">
              Generate your first AI-powered session summary to streamline your documentation
            </p>
            <Button onClick={() => setShowGenerateDialog(true)} className="gap-2">
              <Sparkles className="h-4 w-4" />
              Generate First Summary
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {summaries.map(summary => {
            const sessionInfo = getSessionInfo(summary.session_id);
            const isExpanded = expandedSummary === summary.id;
            
            return (
              <Card key={summary.id} className="bg-card/50 border-border/30">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        {sessionInfo.client} - {sessionInfo.date}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        Generated {summary.generated_at ? format(parseISO(summary.generated_at), "MMM d 'at' h:mm a") : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {summary.is_approved ? (
                        <Badge className="bg-green-500/20 text-green-500 border-green-500/20">
                          <Check className="h-3 w-3 mr-1" />
                          Approved
                        </Badge>
                      ) : (
                        <Button size="sm" onClick={() => handleApproveSummary(summary.id)} className="gap-1">
                          <Check className="h-3 w-3" />
                          Approve
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedSummary(isExpanded ? null : summary.id)}
                      >
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Summary Preview */}
                  <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                    <p className="text-sm text-foreground">{summary.ai_summary}</p>
                  </div>

                  {/* Risk Flags */}
                  {summary.risk_flags && summary.risk_flags.length > 0 && (
                    <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="h-4 w-4 text-destructive" />
                        <span className="font-medium text-destructive">Risk Flags</span>
                      </div>
                      <ul className="list-disc list-inside text-sm text-foreground">
                        {summary.risk_flags.map((flag, i) => (
                          <li key={i}>{flag}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="grid md:grid-cols-2 gap-4 pt-4 border-t border-border/30">
                      {/* Key Themes */}
                      {summary.key_themes && summary.key_themes.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Target className="h-4 w-4 text-primary" />
                            <span className="font-medium text-foreground text-sm">Key Themes</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {summary.key_themes.map((theme, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">{theme}</Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Interventions */}
                      {summary.interventions_used && summary.interventions_used.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <ClipboardList className="h-4 w-4 text-accent" />
                            <span className="font-medium text-foreground text-sm">Interventions Used</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {summary.interventions_used.map((intervention, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{intervention}</Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Client Insights */}
                      {summary.client_insights && summary.client_insights.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Lightbulb className="h-4 w-4 text-amber-500" />
                            <span className="font-medium text-foreground text-sm">Client Insights</span>
                          </div>
                          <ul className="text-sm text-muted-foreground space-y-1">
                            {summary.client_insights.map((insight, i) => (
                              <li key={i}>• {insight}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Homework Suggestions */}
                      {summary.homework_suggestions && summary.homework_suggestions.length > 0 && (
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Edit2 className="h-4 w-4 text-green-500" />
                            <span className="font-medium text-foreground text-sm">Homework Suggestions</span>
                          </div>
                          <ul className="text-sm text-muted-foreground space-y-1">
                            {summary.homework_suggestions.map((hw, i) => (
                              <li key={i}>• {hw}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Follow-up Topics */}
                      {summary.follow_up_topics && summary.follow_up_topics.length > 0 && (
                        <div className="md:col-span-2">
                          <div className="flex items-center gap-2 mb-2">
                            <FileText className="h-4 w-4 text-blue-500" />
                            <span className="font-medium text-foreground text-sm">Follow-up Topics</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {summary.follow_up_topics.map((topic, i) => (
                              <Badge key={i} variant="outline" className="text-xs bg-blue-500/10 border-blue-500/20">{topic}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
